import { useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';

import { useSession, useStopSession, useDeleteSession } from '@/hooks/useSessions';
import { useAuth } from '@/auth/AuthContext';
import { isAdmin } from '@/types';
import { useLiveMetrics } from '@/hooks/useLiveMetrics';
import { useSessionMetrics } from '@/hooks/useSessionMetrics';
import { downloadReport, downloadMetricsCsv } from '@/api/metrics';
import { bucketPoints, bucketByTrafficType, type LiveSample } from '@/lib/metrics';
import { SessionBadge, VerdictBadge } from '@/components/common/StatusBadge';
import { Loading, ErrorBanner, EmptyState } from '@/components/common/States';
import { FindingsPanel, FieldBadges, findingColor } from '@/components/common/Findings';
import type { Finding } from '@/types';
import { ThroughputChart, LatencyLossChart, TrafficTypeChart } from '@/components/charts/LiveCharts';
import { fmtMbps, fmtMs, fmtPct, fmtDuration, relTime, shortId } from '@/lib/format';
import { tosLabel } from '@/lib/trafficProfiles';

// Bufferbloat letter grade → colour (green good … red bad).
function gradeColor(g?: string | null): string {
  switch (g) {
    case 'A': return 'var(--signal)';
    case 'B': return '#8fe388';
    case 'C': return 'var(--warn)';
    case 'D': return '#ff9d4b';
    case 'F': return 'var(--loss)';
    default: return 'var(--muted)';
  }
}

// QoE MOS → colour (green good … red bad).
function mosColor(m?: number | null): string {
  if (m == null) return 'var(--muted)';
  if (m >= 4.0) return 'var(--signal)';
  if (m >= 3.6) return 'var(--warn)';
  return 'var(--loss)';
}

function Readout({
  label,
  value,
  lit,
  color,
  findings,
  field,
}: {
  label: string;
  value: string;
  lit?: boolean;
  color?: string;
  // When both are supplied, any finding that cites this summary field stamps
  // its number here — so the reader can trace a sentence in the analysis panel
  // straight to the figure it was derived from.
  findings?: Finding[] | null;
  field?: string;
}) {
  return (
    <div className="card kpi">
      <div className="k-label">
        {label}
        {field ? <FieldBadges findings={findings} field={field} /> : null}
      </div>
      <div className={`k-value${lit ? ' lit' : ''}`} style={{ fontSize: 22, color }}>{value}</div>
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="spread" style={{ padding: '9px 0', borderBottom: '1px solid var(--line)' }}>
      <span className="label" style={{ letterSpacing: '0.1em' }}>{label}</span>
      <span className="mono dim" style={{ fontSize: 13 }}>{value}</span>
    </div>
  );
}

function ChartArea({ samples, loading, empty }: { samples: LiveSample[]; loading: boolean; empty: string }) {
  return (
    <>
      <div className="panel" style={{ padding: '18px 18px 8px', marginBottom: 18 }}>
        <div className="eyebrow" style={{ marginBottom: 8 }}>throughput · Mbps</div>
        {loading ? <Loading label="Loading metrics" /> : samples.length === 0 ? (
          <div className="dim mono" style={{ fontSize: 13, padding: '24px 4px' }}>{empty}</div>
        ) : (
          <ThroughputChart samples={samples} />
        )}
      </div>
      <div className="panel" style={{ padding: '18px 18px 8px', marginBottom: 22 }}>
        <div className="eyebrow" style={{ marginBottom: 8 }}>latency · ms / loss · % / retransmits/s</div>
        {loading || samples.length === 0 ? (
          <div className="dim mono" style={{ fontSize: 13, padding: '24px 4px' }}>
            {loading ? 'Loading metrics…' : empty}
          </div>
        ) : (
          <LatencyLossChart samples={samples} />
        )}
      </div>
    </>
  );
}

export function SessionDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user: me } = useAuth();
  const delM = useDeleteSession();
  const { data: s, isLoading, error } = useSession(id);
  const stop = useStopSession();

  const active = s?.status === 'active';
  const finished = s?.status === 'completed' || s?.status === 'error';

  const live = useLiveMetrics(id, active);
  const hist = useSessionMetrics(id, !!finished);
  const histSamples = useMemo(
    () => (hist.data ? bucketPoints(hist.data.points) : []),
    [hist.data],
  );
  // Prefer the freshly fetched summary; fall back to the one persisted at
  // /stop so the readouts render instantly while the point series loads.
  const sum = hist.data?.summary ?? s?.summary_json ?? undefined;

  // Per-traffic-class series. Only worth drawing when the run actually mixed
  // classes — for a single-purpose test the merged Download/Upload chart above
  // already says everything, and a second near-identical chart is just noise.
  const perType = useMemo(() => {
    if (!hist.data) return { rows: [], types: [] };
    const typeById: Record<string, string> = {};
    for (const st of sum?.per_stream ?? []) {
      if (st.traffic_type) typeById[st.stream_id] = st.traffic_type;
    }
    return bucketByTrafficType(hist.data.points, typeById);
  }, [hist.data, sum]);

  // Moments a finding refers to, drawn as dashed markers on the class chart.
  const chartMarks = useMemo(
    () =>
      (sum?.findings ?? []).flatMap((f) =>
        (f.marks ?? []).map((m) => ({
          t: new Date(m.ts).getTime(),
          label: m.label ?? '',
          color: findingColor(f.severity),
        })),
      ).filter((m) => Number.isFinite(m.t)),
    [sum],
  );

  const [reportBusy, setReportBusy] = useState(false);
  const [reportErr, setReportErr] = useState<string | null>(null);
  const [csvBusy, setCsvBusy] = useState(false);

  if (isLoading) return <Loading label="Loading session" />;
  if (error) return <ErrorBanner error={error} />;
  if (!s) return <EmptyState title="Session not found" />;

  // Read the last SETTLED second for the live readouts, not live.latest. The
  // in-progress bucket is still filling — the transmitter's frame (send rate,
  // e.g. the 50 Mbps cap) and the receiver's frame (actual, e.g. 46) land a few
  // ms apart — so reading it mid-fill flickers to the cap for an instant each
  // second. The previous bucket is complete, so the readout matches the graph
  // and report exactly.
  const L =
    active && live.samples.length >= 2
      ? live.samples[live.samples.length - 2]
      : live.latest;
  // Prefer the clean directional numbers; fall back to role-based tx/rx for
  // legacy data so old sessions still render sensible values.
  const liveDl = L?.download ?? L?.rx ?? null;
  const liveUl = L?.upload ?? L?.tx ?? null;
  const dlPeak = sum?.download_max_mbps ?? sum?.rx_max_mbps ?? null;
  const ulPeak = sum?.upload_max_mbps ?? sum?.tx_max_mbps ?? null;
  const readouts = active
    ? [
        { label: 'Download', value: fmtMbps(liveDl), lit: true },
        { label: 'Upload', value: fmtMbps(liveUl), lit: true },
        { label: 'Latency', value: fmtMs(L?.latency) },
        { label: 'Jitter', value: fmtMs(L?.jitter) },
        { label: 'Loss', value: fmtPct(L?.loss) },
        { label: 'Retransmits/s', value: L?.retransmits != null ? String(L.retransmits) : '—' },
      ]
    : [
        { label: 'Download peak', value: fmtMbps(dlPeak) },
        { label: 'Upload peak', value: fmtMbps(ulPeak) },
        { label: 'Latency avg', value: fmtMs(sum?.latency_avg_ms) },
        { label: 'Jitter avg', value: fmtMs(sum?.jitter_avg_ms) },
        { label: 'Loss avg', value: fmtPct(sum?.loss_avg_pct) },
        { label: 'Retransmits', value: sum?.retransmits_total != null ? String(sum.retransmits_total) : '—' },
      ];

  async function getReport() {
    if (!id) return;
    setReportBusy(true);
    setReportErr(null);
    try {
      await downloadReport(id);
    } catch (e) {
      setReportErr(e instanceof Error ? e.message : 'Report failed');
    } finally {
      setReportBusy(false);
    }
  }

  async function getCsv() {
    if (!id) return;
    setCsvBusy(true);
    setReportErr(null);
    try {
      await downloadMetricsCsv(id);
    } catch (e) {
      setReportErr(e instanceof Error ? e.message : 'CSV export failed');
    } finally {
      setCsvBusy(false);
    }
  }

  return (
    <div className="fade-up">
      <div className="page-head spread">
        <div>
          <div className="row" style={{ gap: 10 }}>
            <Link to="/sessions" className="btn btn-ghost" style={{ padding: '4px 8px' }}>←</Link>
            <div className="eyebrow">session</div>
          </div>
          <div className="title" style={{ marginTop: 6 }}>{s.label || shortId(s.id)}</div>
          <div className="row wrap" style={{ gap: 10, marginTop: 8 }}>
            <SessionBadge status={s.status} />
            <VerdictBadge verdict={s.verdict} />
            <span className="mono dim" style={{ fontSize: 12 }}>{s.test_mode}</span>
            {s.firmware && <span className="mono dim" style={{ fontSize: 12 }}>FW {s.firmware}</span>}
            {s.tag && <span className="mono dim" style={{ fontSize: 12 }}>#{s.tag}</span>}
            {active && (
              <span className="mono" style={{ fontSize: 12, color: live.connected ? 'var(--signal)' : 'var(--warn)' }}>
                {live.connected ? `▸ live · ${live.streamCount} stream(s)` : '▸ connecting…'}
              </span>
            )}
          </div>
        </div>
        <div className="row" style={{ gap: 10 }}>
          {finished && (
            <button className="btn" onClick={getReport} disabled={reportBusy}>
              {reportBusy ? <span className="spinner" /> : '⤓ Report (PDF)'}
            </button>
          )}
          {finished && (
            <button className="btn btn-ghost" onClick={getCsv} disabled={csvBusy}>
              {csvBusy ? <span className="spinner" /> : '⤓ Data (CSV)'}
            </button>
          )}
          {active && (
            <button className="btn btn-danger" disabled={stop.isPending} onClick={() => stop.mutate(s.id)}>
              {stop.isPending ? <span className="spinner" /> : '■ Stop test'}
            </button>
          )}
          {!active && (isAdmin(me) || me?.can_delete_sessions === true) && (
            <button
              className="btn btn-ghost"
              style={{ color: 'var(--loss)' }}
              disabled={delM.isPending}
              onClick={() => {
                if (!window.confirm('Delete this session? Its metrics are permanently removed.')) return;
                delM.mutate(s.id, { onSuccess: () => nav('/sessions') });
              }}
            >
              {delM.isPending ? <span className="spinner" /> : '🗑 Delete'}
            </button>
          )}
        </div>
      </div>

      {s.error_reason && <div style={{ marginBottom: 18 }}><ErrorBanner error={s.error_reason} /></div>}
      {reportErr && <div style={{ marginBottom: 18 }}><ErrorBanner error={reportErr} /></div>}
      {finished && !s.error_reason && sum?.zero_reason && (
        <div
          className="card"
          style={{
            marginBottom: 18,
            padding: '12px 16px',
            borderLeft: '3px solid var(--warn)',
          }}
        >
          <div className="row" style={{ gap: 8, alignItems: 'center', marginBottom: 4 }}>
            <span style={{ color: 'var(--warn)' }}>⚠</span>
            <span className="eyebrow">why no throughput?</span>
          </div>
          <div className="dim" style={{ fontSize: 13 }}>{sum.zero_reason}</div>
        </div>
      )}

      <div className="kpi-grid" style={{ marginBottom: 22 }}>
        {readouts.map((r) => (
          <Readout key={r.label} label={r.label} value={r.value} lit={r.lit} />
        ))}
      </div>

      {s.verdict_json && s.verdict_json.checks.length > 0 && (
        <div className="card" style={{ marginBottom: 18, padding: '12px 16px' }}>
          <div className="row" style={{ gap: 10, marginBottom: 8, alignItems: 'center' }}>
            <div className="eyebrow">pass / fail</div>
            <VerdictBadge verdict={s.verdict} />
          </div>
          <div className="stack" style={{ gap: 4 }}>
            {s.verdict_json.checks.map((c) => (
              <div key={c.metric} className="mono" style={{ fontSize: 12 }}>
                <span style={{ color: c.ok ? 'var(--signal)' : 'var(--loss)' }}>{c.ok ? '✓' : '✗'}</span>{' '}
                {c.metric} {c.op} {c.threshold}
                <span className="dim"> — actual {c.actual == null ? '—' : c.actual.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {s.streams && s.streams.length > 0 && (
        <div className="card" style={{ marginBottom: 18, padding: '12px 16px' }}>
          <div className="eyebrow" style={{ marginBottom: 8 }}>traffic profile</div>
          <div className="stack" style={{ gap: 4 }}>
            {s.streams.map((st) => (
              <div key={st.id} className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>
                ▸ {st.protocol} · {st.direction} ·{' '}
                {st.bitrate_mbps ? `${st.bitrate_mbps} Mbps` : 'unlimited'}
                {st.packet_size ? ` · ${st.packet_size}B` : ''} · {tosLabel(st.tos ?? 0)}
              </div>
            ))}
          </div>
        </div>
      )}

      {active ? (
        <ChartArea samples={live.samples} loading={live.samples.length === 0} empty="Waiting for first frame…" />
      ) : finished ? (
        <ChartArea samples={histSamples} loading={hist.isLoading} empty="No metrics were recorded for this session." />
      ) : (
        <div className="panel" style={{ padding: '28px 24px', marginBottom: 22, textAlign: 'center' }}>
          <div className="eyebrow">charts</div>
          <div className="dim" style={{ marginTop: 8, fontSize: 14 }}>
            This session hasn’t been started yet.
          </div>
        </div>
      )}

      {/* Only for an actual mix: on a single-class run the merged chart above
          already says everything, and a near-identical second chart is noise. */}
      {finished && perType.types.length > 1 && (
        <div className="panel" style={{ padding: '18px 18px 8px', marginBottom: 22 }}>
          <div className="eyebrow" style={{ marginBottom: 8 }}>
            throughput by traffic class · Mbps
          </div>
          <TrafficTypeChart rows={perType.rows} types={perType.types} marks={chartMarks} />
        </div>
      )}

      {/* Analysis first: the conclusions are what the operator actually wants,
          and burying them under raw numbers defeats the point of computing them. */}
      {finished && sum && <FindingsPanel findings={sum.findings} perStream={sum.per_stream} />}

      {finished && sum && (
        <div className="card" style={{ marginBottom: 18 }}>
          <div className="eyebrow" style={{ marginBottom: 10 }}>summary</div>
          <div className="kpi-grid">
            <Readout label="Download avg / peak" findings={sum.findings} field="download_avg_mbps" value={`${fmtMbps(sum.download_avg_mbps ?? sum.rx_avg_mbps)} / ${fmtMbps(dlPeak)}`} />
            <Readout label="Upload avg / peak" findings={sum.findings} field="upload_avg_mbps" value={`${fmtMbps(sum.upload_avg_mbps ?? sum.tx_avg_mbps)} / ${fmtMbps(ulPeak)}`} />
            <Readout label="Latency avg / max" findings={sum.findings} field="latency_avg_ms" value={`${fmtMs(sum.latency_avg_ms)} / ${fmtMs(sum.latency_max_ms)}`} />
            <Readout label="Latency p95 / p99" findings={sum.findings} field="latency_p95_ms" value={`${fmtMs(sum.latency_p95_ms)} / ${fmtMs(sum.latency_p99_ms)}`} />
            <Readout label="Jitter avg / max" findings={sum.findings} field="jitter_avg_ms" value={`${fmtMs(sum.jitter_avg_ms)} / ${fmtMs(sum.jitter_max_ms)}`} />
            <Readout label="Loss avg / max" findings={sum.findings} field="loss_avg_pct" value={`${fmtPct(sum.loss_avg_pct)} / ${fmtPct(sum.loss_max_pct)}`} />
            <Readout label="TCP retransmits" value={String(sum.retransmits_total ?? 0)} />
            <Readout label="Streams / samples" value={`${sum.stream_count} / ${sum.sample_count}`} />
          </div>

          <div className="eyebrow" style={{ margin: '18px 0 10px' }}>latency under load · bufferbloat</div>
          <div className="kpi-grid">
            <Readout label="Idle latency" findings={sum.findings} field="latency_idle_ms" value={fmtMs(sum.latency_idle_ms)} />
            <Readout label="Loaded latency" findings={sum.findings} field="latency_loaded_ms" value={fmtMs(sum.latency_loaded_ms)} />
            <Readout
              label="Bufferbloat"
              value={sum.bufferbloat_ms != null ? `+${sum.bufferbloat_ms.toFixed(1)} ms` : '—'}
              color={gradeColor(sum.bufferbloat_grade)}
            />
            <Readout
              label="Grade"
              value={sum.bufferbloat_grade ?? '—'}
              color={gradeColor(sum.bufferbloat_grade)}
              lit
            />
          </div>
          <div className="muted mono" style={{ fontSize: 11, marginTop: 8 }}>
            ▸ latency added by saturating the link (loaded p95 − idle floor). Lower is better.
          </div>

          {sum.qoe_mos != null && (
            <>
              <div className="eyebrow" style={{ margin: '18px 0 10px' }}>quality of experience · MOS</div>
              <div className="kpi-grid">
                <Readout label="MOS score" value={sum.qoe_mos.toFixed(2)} color={mosColor(sum.qoe_mos)} lit />
                <Readout label="Rating" value={sum.qoe_rating ?? '—'} color={mosColor(sum.qoe_mos)} />
                <Readout label="Scale" value="1.0 – 4.5" />
              </div>
              <div className="muted mono" style={{ fontSize: 11, marginTop: 8 }}>
                ▸ estimated Mean Opinion Score (ITU-T E-model) from latency, jitter and loss — how a real-time app would feel on this link.
              </div>
            </>
          )}

          {sum.latency_up_avg_ms != null && (
            <>
              <div className="eyebrow" style={{ margin: '18px 0 10px' }}>uplink latency</div>
              <div className="kpi-grid">
                <Readout label="Uplink avg / max" value={`${fmtMs(sum.latency_up_avg_ms)} / ${fmtMs(sum.latency_up_max_ms)}`} />
                <Readout label="Uplink p95" value={fmtMs(sum.latency_up_p95_ms)} />
                <Readout label="Uplink jitter" value={fmtMs(sum.jitter_up_avg_ms)} />
                {sum.loss_up_avg_pct != null && (
                  <Readout label="Uplink loss avg / max" value={`${fmtPct(sum.loss_up_avg_pct)} / ${fmtPct(sum.loss_up_max_pct)}`} />
                )}
              </div>
              <div className="muted mono" style={{ fontSize: 11, marginTop: 8 }}>
                ▸ measured at the transmitter (the side that receives the uplink flow). The main latency/loss block above is downlink.
              </div>
            </>
          )}

          {sum.wifi_samples > 0 && (
            <>
              <div className="eyebrow" style={{ margin: '18px 0 10px' }}>wifi</div>
              <div className="kpi-grid">
                <Readout label="Band" value={sum.wifi_band ?? '—'} />
                <Readout label="Channel" value={sum.wifi_channel != null ? String(sum.wifi_channel) : '—'} />
                <Readout label="RSSI avg" value={sum.wifi_rssi_avg != null ? `${sum.wifi_rssi_avg.toFixed(0)} dBm` : '—'} />
                <Readout label="RSSI min" value={sum.wifi_rssi_min != null ? `${sum.wifi_rssi_min.toFixed(0)} dBm` : '—'} />
                <Readout label="BSSID" value={sum.wifi_bssid ?? '—'} />
                <Readout
                  label="Roams"
                  value={String(sum.roam_count ?? 0)}
                  color={(sum.roam_count ?? 0) > 0 ? 'var(--warn)' : undefined}
                />
                {sum.wifi_standard && <Readout label="Standard" value={sum.wifi_standard} />}
                {sum.wifi_security && <Readout label="Security" value={sum.wifi_security} />}
                {(sum.rx_phy_max_mbps != null || sum.tx_phy_max_mbps != null) && (
                  <Readout
                    label="PHY rate (rx/tx)"
                    value={`${sum.rx_phy_max_mbps ?? '—'} / ${sum.tx_phy_max_mbps ?? '—'} Mbps`}
                  />
                )}
                {sum.cochannel_max != null && (
                  <Readout
                    label="Co-channel APs"
                    value={String(sum.cochannel_max)}
                    color={sum.cochannel_max > 3 ? 'var(--warn)' : undefined}
                  />
                )}
                {sum.thermal_worst && sum.thermal_worst !== 'nominal' && (
                  <Readout
                    label="Thermal (worst)"
                    value={sum.thermal_worst}
                    color={sum.thermal_worst === 'critical' ? 'var(--loss)' : 'var(--warn)'}
                  />
                )}
                {sum.battery_min_pct != null && (
                  <Readout label="Battery (min)" value={`${sum.battery_min_pct}%`} />
                )}
              </div>
              {sum.on_cellular_any && (
                <div className="card" style={{ marginTop: 10, borderLeft: '3px solid var(--loss)', padding: '8px 12px' }}>
                  <span className="mono" style={{ fontSize: 12, color: 'var(--loss)' }}>
                    ⚠ Traffic fell back to cellular during this test — Wi-Fi results are not trustworthy.
                  </span>
                </div>
              )}
              {sum.power_save_any && !sum.on_cellular_any && (
                <div className="mono" style={{ fontSize: 11, color: 'var(--warn)', marginTop: 8 }}>
                  ▸ device was in power-save mode — may cap Wi-Fi throughput.
                </div>
              )}
              {sum.roams && sum.roams.length > 0 && (
                <div className="stack" style={{ gap: 4, marginTop: 10 }}>
                  {sum.roams.map((r, i) => (
                    <div key={i} className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>
                      ↝ {relTime(r.ts)} · {r.from_bssid ?? '?'} → <span style={{ color: 'var(--warn)' }}>{r.to_bssid}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
          {hist.data?.points_truncated && (
            <div className="muted mono" style={{ fontSize: 11, marginTop: 10 }}>
              ▸ chart downsampled — point series capped (summary stats are exact).
            </div>
          )}
        </div>
      )}

      <div className="card" style={{ maxWidth: 520 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>details</div>
        <Meta label="ID" value={s.id} />
        <Meta label="Owner" value={s.created_by_email || s.created_by_name || '—'} />
        <Meta label="Transmitter" value={s.transmitter_agent_id ? shortId(s.transmitter_agent_id) : '—'} />
        <Meta label="Source IP" value={s.source_ip ?? '—'} />
        <Meta label="Duration" value={fmtDuration(s.duration_seconds)} />
        <Meta label="Started" value={s.started_at ? relTime(s.started_at) : '—'} />
        <Meta label="Stopped" value={s.stopped_at ? relTime(s.stopped_at) : '—'} />
        <div className="spread" style={{ padding: '9px 0' }}>
          <span className="label" style={{ letterSpacing: '0.1em' }}>Created</span>
          <span className="mono dim" style={{ fontSize: 13 }}>{relTime(s.created_at)}</span>
        </div>
      </div>

      <div style={{ marginTop: 18 }}>
        <button className="btn btn-ghost" onClick={() => nav('/sessions')}>← Back to sessions</button>
      </div>
    </div>
  );
}
